import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Package, Paperclip, X, CheckCircle, Warning, Spinner, FilePdf, Robot } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { shizaiApi, yen } from './shizaiApi';
import TechsImportSection from './TechsImportSection';
import ShippingAllocBadge from './ShippingAllocBadge';
import RegisterAsAssetDialog from './RegisterAsAssetDialog';
import { todayJst, jstDate } from '@/utils/jstDate';
import { decideLinePrice, parseYen, looksLikePlaceholderUnit } from './arrivalLinePrice';

/**
 * 入荷確認 — shown when a PIC marks a request as 「入荷済」.
 * Fixed model (Feb 2026): each line holds three independent numeric fields —
 *   入荷数量 (readonly), 購入単価, 実際購入金額（合計） — no more
 *   quantity-as-price fallback (¥1 bug root cause). Supplier is per-line.
 */


function fmtYen(n) {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return '未設定';
  const num = Number(n);
  return `¥${num.toLocaleString('ja-JP', { maximumFractionDigits: 4 })}`;
}


export function initialLine(ln) {
  const { unit, amount, source } = decideLinePrice(ln);
  return {
    qty: Number(ln.qty) || 0,
    unitStr: unit !== null ? String(Number(unit.toFixed(4))) : '',
    amountStr: amount !== null ? String(Number(amount.toFixed(4))) : '',
    manualAmount: false,
    manualUnit: false,
    source,
    supplierId: ln.techs_supplier_id || ln.arrival_supplier_id || '',
    supplierName: ln.techs_supplier_name || ln.arrival_supplier_name || '',
    techsDenpyoNo: ln.techs_denpyo_no || null,
    techsSourceFile: ln.techs_source_file || null,
  };
}

export default function ArrivalConfirmDialog({ req, onClose, onSaved }) {
  const [preview, setPreview] = useState(null);
  // 入荷確認の直後に開く 台帳登録（IT資産 / 備品）— 台ごとの製造番号を入力する
  const [bridge, setBridge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState({});          // per-line: {qty, unitStr, amountStr, manualAmount, ...}
  // §8.7 既にある台帳レコードを「紐付ける / 新規作成」— 既定は紐付け（増やさない）
  const [unitLinks, setUnitLinks] = useState({});
  const [invoiceNo, setInvoiceNo] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [currency, setCurrency] = useState('JPY');
  const [fxJpyTotal, setFxJpyTotal] = useState('');
  const [fxRate, setFxRate] = useState('');
  const [taxMode, setTaxMode] = useState('');          // '' = 税区分未設定
  const [taxRate, setTaxRate] = useState('');
  const [taxAmount, setTaxAmount] = useState('');
  const [extras, setExtras] = useState([]);            // [{kind, label, amount}]
  const [aiReading, setAiReading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [notes, setNotes] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const initFromPreview = (p) => {
    const init = {};
    (p.lines || []).forEach((ln) => { init[ln.index] = initialLine(ln); });
    setRows(init);
    // 既にある台帳レコードは既定で紐付ける（黙って増やさない）
    const links = {};
    (p.lines || []).forEach((ln) => {
      const ex = ln.unit_plan?.existing || [];
      if (ex.length) links[ln.index] = ex.slice(0, ln.unit_plan.want).map((r) => r.id);
    });
    setUnitLinks(links);
  };

  useEffect(() => {
    let cancelled = false;
    Promise.all([shizaiApi.arrivalPreview(req.id), shizaiApi.suppliers().catch(() => [])])
      .then(([p, sups]) => {
        if (cancelled) return;
        setPreview(p);
        setSuppliers(Array.isArray(sups) ? sups : []);
        initFromPreview(p);
      })
      .catch((e) => {
        toast.error(e?.response?.data?.detail || '入荷情報の取得に失敗しました');
        onClose?.();
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [req.id, onClose]);

  const reloadAfterImport = async () => {
    try {
      const p = await shizaiApi.arrivalPreview(req.id);
      setPreview(p);
      initFromPreview(p);
    } catch { /* keep silent — import succeeded upstream */ }
  };

  const uploadInvoice = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const r = await shizaiApi.uploadPicInvoice(file);
      setAttachment(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '請求書のアップロードに失敗しました');
    } finally { setUploading(false); }
  };

  // Update helpers ---------------------------------------------------------
  const updateUnit = (idx, next) => {
    setRows((r) => {
      const cur = r[idx];
      const unitN = parseYen(next);
      // if user hasn't manually edited amount → recompute amount = unit × qty
      let amountStr = cur.amountStr;
      if (!cur.manualAmount && unitN !== null && cur.qty > 0) {
        amountStr = String(Number((unitN * cur.qty).toFixed(4)));
      }
      return { ...r, [idx]: { ...cur, unitStr: next, amountStr, manualUnit: true } };
    });
  };
  const updateAmount = (idx, next) => {
    setRows((r) => {
      const cur = r[idx];
      return { ...r, [idx]: { ...cur, amountStr: next, manualAmount: true } };
    });
  };
  const updateSupplier = (idx, sid) => {
    setRows((r) => ({ ...r, [idx]: { ...r[idx], supplierId: sid } }));
  };

  // Summary ----------------------------------------------------------------
  const summary = useMemo(() => {
    let items = 0, qtySum = 0, amtSum = 0;
    const sups = new Set();
    Object.values(rows).forEach((r) => {
      items += 1;
      qtySum += Number(r.qty) || 0;
      const a = parseYen(r.amountStr);
      if (a !== null) amtSum += a;
      if (r.supplierId) sups.add(`id:${r.supplierId}`);
      else if (r.supplierName) sups.add(`name:${r.supplierName}`);
    });
    return { items, qtySum, amtSum, supplierCount: sups.size };
  }, [rows]);

  // Validation helpers -----------------------------------------------------
  const lineWarning = (idx) => {
    const r = rows[idx];
    if (!r) return null;
    const amt = parseYen(r.amountStr);
    const unit = parseYen(r.unitStr);
    const qty = Number(r.qty) || 0;
    if (amt === null) return null;
    if (qty > 0 && amt > 0 && amt === qty && unit === null) {
      return '購入金額が入荷数量と同じ値になっています。取込価格を確認してください。';
    }
    if (unit !== null && qty > 0 && amt > 0) {
      const expected = unit * qty;
      const diff = Math.abs(expected - amt);
      const tol = Math.max(1, expected * 0.01); // 1% or ¥1
      if (diff > tol) return `TECHSの発注単価と購入金額の計算結果が一致しません（期待値: ¥${expected.toLocaleString()}）。`;
    }
    return null;
  };

  // 通貨・税・諸費用から、その場で円の支払合計を出す（サーバー側と同じ考え方）
  const calc = useMemo(() => {
    const extraSum = extras.reduce((a, x) => a + (parseYen(x.amount) || 0), 0);
    const tax = parseYen(taxAmount) || 0;
    const taxOnTop = taxMode === 'excluded' ? tax : 0;
    const grand = (summary?.amtSum || 0) + extraSum + taxOnTop;
    let rateUsed = null;
    if (currency !== 'JPY') {
      const jpyTotal = parseYen(fxJpyTotal);
      if (jpyTotal && grand > 0) rateUsed = jpyTotal / grand;
      else if (parseYen(fxRate)) rateUsed = parseYen(fxRate);
    }
    const paidJpy = currency === 'JPY' ? grand : (rateUsed ? Math.round(grand * rateUsed) : null);
    return { extraSum, taxSum: tax, rateUsed, paidJpy };
  }, [extras, taxAmount, taxMode, summary, currency, fxJpyTotal, fxRate]);

  const readInvoiceAi = async () => {
    if (!attachment?.photo_id) return;
    setAiReading(true);
    try {
      const r = await shizaiApi.invoiceRead(req.id, attachment.photo_id);
      setAiResult(r);
      toast.success('請求書を読み取りました。内容を確認して「反映」してください', {
        description: r.model ? `使用モデル: ${r.model}` : undefined,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || '請求書を読み取れませんでした');
    } finally { setAiReading(false); }
  };

  const applyAi = () => {
    const s = aiResult?.suggestion;
    if (!s) return;
    if (s.currency) setCurrency(s.currency);
    if (s.invoice_no) setInvoiceNo(s.invoice_no);
    if (s.invoice_date) setInvoiceDate(s.invoice_date);
    if (s.tax_mode) setTaxMode(s.tax_mode);
    if (s.tax_rate != null) setTaxRate(String(s.tax_rate));
    if (s.tax_amount != null) setTaxAmount(String(s.tax_amount));
    if ((s.extra_costs || []).length) {
      setExtras(s.extra_costs.map((x) => ({ kind: x.kind, label: x.label || '', amount: String(x.amount) })));
    }
    (s.lines || []).forEach((l) => updateAmount(l.index, String(l.actual_price)));
    toast.success(`請求書の内容を反映しました（明細 ${(s.lines || []).length} 件）`);
    setAiResult(null);
  };

  const submit = async () => {    if (preview.invoice_required && !attachment && !preview.has_procurement_invoice) {
      toast.error('ライセンスの入荷確認には請求書の添付が必須です');
      return;
    }
    const linesPayload = [];
    for (const ln of preview.lines) {
      const r = rows[ln.index];
      const amt = parseYen(r?.amountStr);
      const unit = parseYen(r?.unitStr);
      if (amt === null || !(amt >= 0)) {
        toast.error(`「${ln.name}」の実際購入金額を入力してください（必須・0以上）`);
        return;
      }
      linesPayload.push({
        index: ln.index,
        actual_price: amt,
        purchase_unit_price: unit !== null && unit > 0 ? unit : null,
        supplier_id: r?.supplierId || null,
        supplier_name: !r?.supplierId ? (r?.supplierName?.trim() || null) : null,
        manual_price_override: !!r?.manualAmount,
        stock_qty: (r?.stockQty === undefined || r?.stockQty === ''
          ? null : Number(r.stockQty)),
      });
    }
    setSaving(true);
    try {
      const res = await shizaiApi.confirmArrival(req.id, {
        lines: linesPayload,
        unit_links: unitLinks,
        invoice_no: invoiceNo.trim() || null,
        invoice_date: invoiceDate || null,
        currency,
        fx_jpy_total: currency === 'JPY' ? null : (parseYen(fxJpyTotal) ?? null),
        fx_rate: currency === 'JPY' ? null : (parseYen(fxRate) ?? null),
        tax_mode: taxMode || null,
        tax_rate: parseYen(taxRate) ?? null,
        tax_amount: parseYen(taxAmount) ?? null,
        extra_costs: extras
          .map((x) => ({ kind: x.kind, label: x.label?.trim() || null, amount: parseYen(x.amount) }))
          .filter((x) => x.amount !== null && x.amount > 0),
        supplier_id: null,        // 明細ごとに保持するためヘッダーは未指定
        supplier_name: null,
        notes: notes.trim() || null,
        invoice_photo_url: attachment?.photo_url || null,
        invoice_photo_filename: attachment?.filename || null,
      });
      const r = res.result || {};
      const parts = ['入荷を登録しました'];
      if ((r.updated || []).length) parts.push(`マスター更新 ${r.updated.length}件`);
      if ((r.unmatched || []).length) parts.push(`未登録（履歴のみ記録） ${r.unmatched.length}件`);
      toast.success(parts.join(' — '), {
        description: res.target_label ? `反映先: ${res.target_label}` : undefined,
      });
      // 設備（durable）の台帳は1台=1行。数量分の行と台ごとの製造番号を
      // 続けて登録できるよう、既存の 台帳登録 ブリッジをそのまま開く。
      if (preview.target_master === 'it' || preview.target_master === 'office') {
        setBridge({
          ledger: preview.target_master,
          order: {
            id: req.id,
            request_no: req.request_no,
            order_no: req.request_no,
            received_at: todayJst(),
            supplier_name: rows[preview.lines[0]?.index]?.supplierName || null,
            lines: preview.lines.map((ln) => ({
              name: ln.name,
              part_no: ln.part_no,
              qty: ln.qty,
              uom: ln.uom,
              unit_price: parseYen(rows[ln.index]?.unitStr) || ln.unit_price || 0,
              amount: parseYen(rows[ln.index]?.amountStr) || 0,
            })),
          },
        });
        return;
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '入荷確認の登録に失敗しました');
    } finally { setSaving(false); }
  };

  return (
    <>
    <Dialog open onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="arrival-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Package size={20} weight="duotone" className="text-emerald-600" />
            入荷確認
            <span className="font-mono text-xs text-zinc-500">{req.request_no}</span>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-sm text-zinc-400">読み込み中...</div>
        ) : (
          <div className="space-y-4">
            {preview.target_label ? (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-xs text-emerald-800 flex items-center gap-1.5"
                data-testid="arrival-target-label">
                <CheckCircle size={14} weight="fill" className="shrink-0" />
                実際購入金額は <span className="font-bold">{preview.target_label}</span> の原価へ自動反映されます
              </div>
            ) : (
              <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs text-amber-800 flex items-center gap-1.5"
                data-testid="arrival-target-none">
                <Warning size={14} weight="fill" className="shrink-0" />
                この受注分類には反映先マスターが未設定です — 購入価格履歴のみ記録されます（設定 → 分類 で変更できます）
              </div>
            )}

            {preview.procurement_method && preview.procurement_method !== 'undecided' && (
              <div className="text-[11px] text-zinc-500 px-1" data-testid="arrival-proc-method">
                調達方法: <span className="font-semibold text-zinc-700">{preview.procurement_method_label}</span>
                {preview.invoice_required && <span className="text-rose-600 ml-1">— 請求書の添付が必須です</span>}
                {preview.invoice_recommended && <span className="text-amber-600 ml-1">— 請求書の添付を推奨します</span>}
              </div>
            )}

            {req.delivery_destination?.name && (
              <div className="text-[11px] text-zinc-500 px-1" data-testid="arrival-delivery-dest">
                納入先: <span className="font-semibold text-zinc-700">{req.delivery_destination.name}</span>
                <span className="font-mono text-[10px] text-zinc-400 ml-1.5">{req.delivery_destination.code}</span>
              </div>
            )}

            {/* TECHS実績取込（調達方法=TECHS のみ・任意） */}
            {preview.procurement_method === 'techs' && (
              <TechsImportSection req={req} onApplied={reloadAfterImport}
                hasAllocation={(preview.lines || []).some((l) => l.shipping_allocation)} />
            )}

            {/* Per-line detail cards */}
            <div className="space-y-2.5">
              {preview.lines.map((ln) => {
                const match = ln.master_match;
                const r = rows[ln.index] || {};
                const warning = lineWarning(ln.index);
                return (
                  <div key={ln.index} className="rounded-lg border border-zinc-200 p-3 space-y-2"
                    data-testid={`arrival-line-${ln.index}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-zinc-900 min-w-0">
                        {ln.name}
                        {ln.part_no && <span className="ml-1.5 font-mono text-[10px] text-zinc-400">{ln.part_no}</span>}
                      </p>
                      <div className="flex items-center gap-2 shrink-0">
                        <ShippingAllocBadge alloc={ln.shipping_allocation}
                          status={ln.shipping_allocation_status} lineIndex={ln.index} />
                        <span className="text-[11px] text-zinc-500 tabular-nums">入荷数量: {ln.qty} {ln.uom}</span>
                      </div>
                    </div>

                    {ln.existing_purchase_record && (
                      <div className="rounded-md border border-sky-200 bg-sky-50 px-2 py-1.5"
                        data-testid={`arrival-existing-record-${ln.index}`}>
                        <p className="text-[11px] font-semibold text-sky-800">
                          前回の入荷記録を更新します（増えません）
                        </p>
                        <p className="text-[10.5px] text-sky-700 tabular-nums">
                          前回: {ln.existing_purchase_record.qty} {ln.uom} ・
                          {fmtYen(ln.existing_purchase_record.actual_price)} ・
                          単価 {fmtYen(ln.existing_purchase_record.unit_price)}
                          （{jstDate(ln.existing_purchase_record.received_date)}）
                          {' → '}今回: {r.qty ?? ln.qty} {ln.uom} ・{fmtYen(parseYen(r.amountStr))}
                        </p>
                      </div>
                    )}

                    {ln.unit_plan?.existing?.length > 0 && (
                      <div className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 space-y-1"
                        data-testid={`arrival-unit-existing-${ln.index}`}>
                        <p className="text-[11px] font-semibold text-amber-900">
                          この明細の{ln.unit_plan.label}はすでに
                          {ln.unit_plan.existing.length}件 登録されています
                          （台数 {ln.unit_plan.want}）
                        </p>
                        <p className="text-[10.5px] text-amber-800">
                          チェックした分は既存の台帳に紐付け、足りない分だけ新しく作ります。
                          外すとその分が新規作成になります。
                        </p>
                        {ln.unit_plan.existing.map((rec) => {
                          const on = (unitLinks[ln.index] || []).includes(rec.id);
                          return (
                            <label key={rec.id}
                              className="flex items-center gap-2 text-[11px] text-amber-900"
                              data-testid={`arrival-unit-link-${ln.index}-${rec.id}`}>
                              <input type="checkbox" checked={on}
                                data-testid={`arrival-unit-link-cb-${ln.index}-${rec.id}`}
                                onChange={(e) => setUnitLinks((prev) => {
                                  const cur = prev[ln.index] || [];
                                  return {
                                    ...prev,
                                    [ln.index]: e.target.checked
                                      ? [...cur, rec.id] : cur.filter((x) => x !== rec.id),
                                  };
                                })} />
                              <span className="font-mono">{rec.no}</span>
                              <span className="truncate">{rec.name}</span>
                              {rec.location && <span className="text-amber-700">（{rec.location}）</span>}
                              <span className="ml-auto tabular-nums text-amber-700">
                                {rec.price != null ? fmtYen(rec.price) : '—'}
                              </span>
                            </label>
                          );
                        })}
                        <p className="text-[10.5px] font-semibold text-amber-900"
                          data-testid={`arrival-unit-create-count-${ln.index}`}>
                          新しく作る: {Math.max(0, ln.unit_plan.want - ln.unit_plan.linked
                            - (unitLinks[ln.index] || []).length)}件
                        </p>
                      </div>
                    )}

                    {ln.stock_plan && (
                      <div className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1.5"
                        data-testid={`arrival-stock-${ln.index}`}>
                        <p className="text-[11px] font-semibold text-emerald-900">
                          {ln.stock_plan.needs_qty_input
                            ? `在庫: ${ln.stock_plan.label}`
                            : (r.stockQty !== undefined && r.stockQty !== ''
                              ? `${Number(r.stockQty)} ${ln.stock_plan.unit} を在庫に追加します`
                              : ln.stock_plan.label)}
                        </p>
                        <p className="text-[10.5px] text-emerald-700 tabular-nums">
                          {ln.stock_plan.item_name} ・在庫 {ln.stock_plan.stock_before}
                          {ln.stock_plan.unit}
                          {' → '}
                          {(() => {
                            const add = r.stockQty !== undefined && r.stockQty !== ''
                              ? Number(r.stockQty) : ln.stock_plan.stock_qty;
                            return add === null || add === undefined || Number.isNaN(add)
                              ? '—' : `${Number(ln.stock_plan.stock_before) + Number(add)}${ln.stock_plan.unit}`;
                          })()}
                        </p>
                        {ln.stock_plan.needs_qty_input && (
                          <div className="mt-1 flex items-center gap-1.5">
                            <span className="text-[10.5px] text-emerald-800">受入数量（{ln.stock_plan.unit}）</span>
                            <Input type="number" min="0" value={r.stockQty ?? ''}
                              onChange={(e) => setRows((prev) => ({
                                ...prev, [ln.index]: { ...prev[ln.index], stockQty: e.target.value },
                              }))}
                              className="h-8 w-24 text-[12px]"
                              data-testid={`arrival-stock-qty-${ln.index}`} />
                          </div>
                        )}
                      </div>
                    )}

                    {ln.techs_unit_conversion && (
                      <p className="text-[10.5px] text-amber-700 tabular-nums"
                        data-testid={`arrival-techs-conversion-${ln.index}`}>
                        TECHS: {ln.techs_unit_conversion.techs_qty} ×{' '}
                        {fmtYen(ln.techs_unit_conversion.techs_unit_price)} →{' '}
                        {ln.techs_unit_conversion.line_qty} {ln.techs_unit_conversion.line_uom} ×{' '}
                        {fmtYen(ln.techs_unit_conversion.line_unit_price)}
                      </p>
                    )}

                    {preview.target_master && (
                      <div>
                        {match ? (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-semibold"
                            data-testid={`arrival-match-${ln.index}`}>
                            <CheckCircle size={10} weight="fill" /> マスター登録済: {match.name}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-semibold"
                            data-testid={`arrival-nomatch-${ln.index}`}>
                            <Warning size={10} weight="fill" /> マスター未登録 — 購入価格履歴のみ記録
                          </span>
                        )}
                      </div>
                    )}

                    {/* Prices — 3-column layout */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1">
                      <div>
                        <Label className="text-[10px] font-semibold text-zinc-500">入荷数量</Label>
                        <div className="mt-1 h-9 px-2.5 flex items-center rounded-md bg-zinc-50 border border-zinc-200 text-sm text-zinc-800 tabular-nums"
                          data-testid={`arrival-qty-${ln.index}`}>
                          {ln.qty} <span className="text-zinc-500 ml-1">{ln.uom}</span>
                        </div>
                      </div>
                      <div>
                        <Label className="text-[10px] font-semibold text-zinc-500">購入単価</Label>
                        <Input
                          inputMode="decimal" type="text"
                          value={r.unitStr ?? ''}
                          onChange={(e) => updateUnit(ln.index, e.target.value)}
                          placeholder="例: 16980"
                          className="h-9 mt-1 text-right tabular-nums font-medium"
                          data-testid={`arrival-unit-${ln.index}`} />
                      </div>
                      <div>
                        <Label className="text-[10px] font-semibold text-zinc-500">
                          実際購入金額（{currency === 'JPY' ? '円' : currency}・合計）<span className="text-rose-500 ml-0.5">＊必須</span>
                        </Label>
                        <Input
                          inputMode="decimal" type="text"
                          value={r.amountStr ?? ''}
                          onChange={(e) => updateAmount(ln.index, e.target.value)}
                          placeholder="実費を入力"
                          className="h-9 mt-1 text-right tabular-nums font-semibold text-zinc-900 bg-emerald-50/40 border-emerald-200 focus-visible:ring-emerald-300"
                          data-testid={`arrival-price-${ln.index}`} />
                      </div>
                    </div>

                    {/* Badges + trace */}
                    <div className="flex items-center gap-1.5 flex-wrap text-[10px]">
                      {r.source === 'techs' && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200 font-semibold"
                          data-testid={`arrival-techs-badge-${ln.index}`}>
                          TECHS取込
                        </span>
                      )}
                      {r.manualAmount && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 font-semibold"
                          data-testid={`arrival-manual-badge-${ln.index}`}>
                          手動変更
                        </span>
                      )}
                      {r.techsDenpyoNo && (
                        <span className="text-zinc-500">
                          発注伝票No: <span className="font-mono">{r.techsDenpyoNo}</span>
                        </span>
                      )}
                      {ln.techs_amount != null && (
                        <span className="text-zinc-400">
                          TECHS実績金額: <span className="tabular-nums">{yen(ln.techs_amount)}</span>
                        </span>
                      )}
                    </div>

                    {/* Supplier selector (per-line) */}
                    <div>
                      <Label className="text-[10px] font-semibold text-zinc-500">仕入先（この明細）</Label>
                      <select
                        value={r.supplierId || ''}
                        onChange={(e) => updateSupplier(ln.index, e.target.value)}
                        className="mt-1 h-9 w-full rounded-md border border-zinc-300 bg-white px-2 text-sm text-zinc-800 outline-none focus:ring-2 focus:ring-emerald-200"
                        data-testid={`arrival-supplier-${ln.index}`}
                      >
                        <option value="">
                          {r.supplierName ? `未登録: ${r.supplierName}` : '未指定'}
                        </option>
                        {suppliers.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}{s.code ? ` (${s.code})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>

                    {warning && (
                      <div className="rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-[11px] text-rose-700 flex items-start gap-1"
                        data-testid={`arrival-warning-${ln.index}`}>
                        <Warning size={12} weight="fill" className="shrink-0 mt-[1px]" /> {warning}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Sticky purchase summary */}
            <div className="sticky bottom-0 -mx-4 sm:mx-0 mt-3 rounded-lg border border-zinc-200 bg-white shadow-sm px-3 py-2"
              data-testid="arrival-summary">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <div>
                  <p className="text-[10px] text-zinc-500">品目数</p>
                  <p className="font-bold tabular-nums text-zinc-900" data-testid="arrival-summary-items">{summary.items}</p>
                </div>
                <div>
                  <p className="text-[10px] text-zinc-500">入荷数量合計</p>
                  <p className="font-bold tabular-nums text-zinc-900" data-testid="arrival-summary-qty">{summary.qtySum}</p>
                </div>
                <div>
                  <p className="text-[10px] text-zinc-500">
                    実際購入金額合計{currency !== 'JPY' ? `（${currency}）` : ''}
                  </p>
                  <p className="font-bold tabular-nums text-emerald-700" data-testid="arrival-summary-amount">
                    {currency === 'JPY' ? fmtYen(summary.amtSum)
                      : `${summary.amtSum.toLocaleString('ja-JP')} ${currency}`}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] text-zinc-500">仕入先数</p>
                  <p className="font-bold tabular-nums text-zinc-900" data-testid="arrival-summary-suppliers">{summary.supplierCount}</p>
                </div>
              </div>
            </div>

            {/* Header fields */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs font-semibold text-zinc-600">請求書番号（任意）</Label>
                <Input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)}
                  maxLength={64} placeholder="例: INV-2026-0123" className="h-9 mt-1"
                  data-testid="arrival-invoice-no" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-zinc-600">請求書日付（任意）</Label>
                <Input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)}
                  className="h-9 mt-1" data-testid="arrival-invoice-date" />
                <p className="mt-1 text-[10px] text-zinc-400">月次の請求書パックはこの日付で集計します。</p>
              </div>
              <div>
                <Label className="text-xs font-semibold text-zinc-600">備考（任意）</Label>
                <Input value={notes} onChange={(e) => setNotes(e.target.value)}
                  maxLength={2000} placeholder="任意" className="h-9 mt-1"
                  data-testid="arrival-notes" />
              </div>
            </div>

            {/* 請求書の内訳 — 通貨 / 税 / 諸費用。すべて任意（既定のままなら従来と同じ） */}
            <div className="rounded-lg border border-zinc-200 bg-zinc-50/60 p-3 space-y-3"
              data-testid="arrival-invoice-breakdown">
              <p className="text-xs font-bold text-zinc-700">請求書の内訳（任意）</p>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div>
                  <Label className="text-[10px] font-semibold text-zinc-500">通貨</Label>
                  <select value={currency} onChange={(e) => setCurrency(e.target.value)}
                    className="mt-1 h-9 w-full rounded-md border border-zinc-300 bg-white px-2 text-sm"
                    data-testid="arrival-currency">
                    {['JPY', 'USD', 'EUR', 'CNY', 'KRW', 'TWD', 'GBP', 'THB', 'VND'].map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-[10px] font-semibold text-zinc-500">税区分</Label>
                  <select value={taxMode} onChange={(e) => setTaxMode(e.target.value)}
                    className="mt-1 h-9 w-full rounded-md border border-zinc-300 bg-white px-2 text-sm"
                    data-testid="arrival-tax-mode">
                    <option value="">未設定</option>
                    <option value="included">税込（金額に税を含む）</option>
                    <option value="excluded">税抜（税は別）</option>
                  </select>
                </div>
                <div>
                  <Label className="text-[10px] font-semibold text-zinc-500">税率（%）</Label>
                  <Input inputMode="decimal" value={taxRate} onChange={(e) => setTaxRate(e.target.value)}
                    placeholder="例: 10" className="h-9 mt-1 text-right tabular-nums"
                    data-testid="arrival-tax-rate" />
                </div>
                <div>
                  <Label className="text-[10px] font-semibold text-zinc-500">
                    税額（{currency === 'JPY' ? '円' : currency}）
                  </Label>
                  <Input inputMode="decimal" value={taxAmount} onChange={(e) => setTaxAmount(e.target.value)}
                    placeholder="例: 1698" className="h-9 mt-1 text-right tabular-nums"
                    data-testid="arrival-tax-amount" />
                </div>
              </div>

              {currency !== 'JPY' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] font-semibold text-zinc-500">
                      実際に請求された円総額<span className="text-emerald-600 ml-1">（こちらが優先）</span>
                    </Label>
                    <Input inputMode="decimal" value={fxJpyTotal} onChange={(e) => setFxJpyTotal(e.target.value)}
                      placeholder="例: 173800" className="h-9 mt-1 text-right tabular-nums"
                      data-testid="arrival-fx-jpy-total" />
                  </div>
                  <div>
                    <Label className="text-[10px] font-semibold text-zinc-500">
                      為替レート（1 {currency} = ○ 円）
                    </Label>
                    <Input inputMode="decimal" value={fxRate} onChange={(e) => setFxRate(e.target.value)}
                      placeholder="例: 155.20" className="h-9 mt-1 text-right tabular-nums"
                      data-testid="arrival-fx-rate" />
                    <p className="mt-1 text-[10px] text-zinc-400">
                      自動取得はしません。円総額が入っていればそちらで換算します。
                    </p>
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-[10px] font-semibold text-zinc-500">
                    諸費用（送料・銀行手数料など／{currency === 'JPY' ? '円' : currency}）
                  </Label>
                  <button type="button" onClick={() => setExtras((x) => [...x, { kind: 'freight', label: '', amount: '' }])}
                    className="text-[11px] font-semibold text-teal-700 hover:underline"
                    data-testid="arrival-extra-add">＋ 追加</button>
                </div>
                {extras.map((x, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`arrival-extra-row-${i}`}>
                    <select value={x.kind}
                      onChange={(e) => setExtras((arr) => arr.map((y, j) => (j === i ? { ...y, kind: e.target.value } : y)))}
                      className="h-9 w-32 rounded-md border border-zinc-300 bg-white px-2 text-sm"
                      data-testid={`arrival-extra-kind-${i}`}>
                      <option value="freight">送料</option>
                      <option value="bank_fee">銀行手数料</option>
                      <option value="customs">通関費用</option>
                      <option value="insurance">保険料</option>
                      <option value="other">その他費用</option>
                    </select>
                    <Input value={x.label} placeholder="請求書の表記（任意）"
                      onChange={(e) => setExtras((arr) => arr.map((y, j) => (j === i ? { ...y, label: e.target.value } : y)))}
                      className="h-9 flex-1" data-testid={`arrival-extra-label-${i}`} />
                    <Input inputMode="decimal" value={x.amount} placeholder="金額"
                      onChange={(e) => setExtras((arr) => arr.map((y, j) => (j === i ? { ...y, amount: e.target.value } : y)))}
                      className="h-9 w-28 text-right tabular-nums" data-testid={`arrival-extra-amount-${i}`} />
                    <button type="button" onClick={() => setExtras((arr) => arr.filter((_, j) => j !== i))}
                      className="text-zinc-400 hover:text-rose-600" data-testid={`arrival-extra-remove-${i}`}>
                      <X size={13} weight="bold" />
                    </button>
                  </div>
                ))}
                {extras.length > 0 && (
                  <p className="text-[10px] text-zinc-500" data-testid="arrival-extra-note">
                    諸費用は品目均等按分（TECHS送料配賦と同じ）で各明細に配賦し、原価に含めます。
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 border-t border-zinc-200 pt-2 text-xs">
                <div>
                  <p className="text-[10px] text-zinc-500">商品合計</p>
                  <p className="font-bold tabular-nums" data-testid="arrival-calc-goods">
                    {currency === 'JPY' ? fmtYen(summary.amtSum) : `${summary.amtSum.toLocaleString('ja-JP')} ${currency}`}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] text-zinc-500">諸費用</p>
                  <p className="font-bold tabular-nums" data-testid="arrival-calc-extras">
                    {currency === 'JPY' ? fmtYen(calc.extraSum) : `${calc.extraSum.toLocaleString('ja-JP')} ${currency}`}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] text-zinc-500">
                    税{taxMode === 'excluded' ? '（別途）' : taxMode === 'included' ? '（内税）' : '（未設定）'}
                  </p>
                  <p className="font-bold tabular-nums" data-testid="arrival-calc-tax">
                    {currency === 'JPY' ? fmtYen(calc.taxSum) : `${calc.taxSum.toLocaleString('ja-JP')} ${currency}`}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] text-zinc-500">支払合計（円）</p>
                  <p className="font-bold tabular-nums text-teal-700" data-testid="arrival-calc-paid">
                    {calc.paidJpy === null ? '円換算待ち' : fmtYen(calc.paidJpy)}
                  </p>
                </div>
              </div>
              {currency !== 'JPY' && calc.rateUsed && (
                <p className="text-[10px] text-zinc-500" data-testid="arrival-calc-rate">
                  換算レート 1 {currency} ≒ {calc.rateUsed.toFixed(3)} 円
                  {fxJpyTotal ? '（円総額から逆算）' : '（入力レート）'} — 原価・月次集計はこの円金額を使います。
                </p>
              )}
            </div>

            {/* Invoice attachment */}
            <div>
              <Label className="text-xs font-semibold text-zinc-600">
                請求書添付
                {preview.invoice_required
                  ? (preview.has_procurement_invoice
                    ? <span className="text-emerald-600 ml-1">（調達方法で添付済み）</span>
                    : <span className="text-rose-500 ml-1">＊必須</span>)
                  : preview.invoice_recommended
                    ? <span className="text-amber-600 ml-1">（推奨）</span>
                    : '（任意）'}
              </Label>
              {attachment ? (
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <div className="inline-flex items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-2.5 py-1.5 text-xs"
                    data-testid="arrival-attachment-chip">
                    {attachment.is_pdf
                      ? <FilePdf size={14} weight="duotone" className="text-rose-600" />
                      : <Paperclip size={14} className="text-zinc-500" />}
                    <span className="max-w-[220px] truncate">{attachment.filename}</span>
                    <button type="button" onClick={() => { setAttachment(null); setAiResult(null); }}
                      className="text-zinc-400 hover:text-rose-600" data-testid="arrival-attachment-remove">
                      <X size={12} weight="bold" />
                    </button>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={readInvoiceAi}
                    disabled={aiReading}
                    className="h-8 text-xs border-teal-300 text-teal-800 hover:bg-teal-50"
                    data-testid="arrival-ai-read">
                    {aiReading
                      ? <><Spinner size={13} className="animate-spin mr-1" /> 読み取り中...</>
                      : <><Robot size={14} weight="duotone" className="mr-1" /> 請求書をAIで読み取る</>}
                  </Button>
                </div>
              ) : (
                <label className="mt-1 flex items-center gap-2 rounded-md border border-dashed border-zinc-300 px-3 py-2 text-xs text-zinc-500 cursor-pointer hover:bg-zinc-50 hover:border-emerald-300 transition-colors">
                  {uploading ? <Spinner size={14} className="animate-spin" /> : <Paperclip size={14} />}
                  {uploading ? 'アップロード中...' : '画像 / PDF を選択（12MBまで）'}
                  <input type="file" accept="image/*,.pdf,.heic,.heif" className="hidden"
                    onChange={(e) => { uploadInvoice(e.target.files?.[0]); e.target.value = ''; }}
                    disabled={uploading}
                    data-testid="arrival-invoice-file" />
                </label>
              )}

              {/* AI読み取り結果 — 確認して反映（自動では何も書かない） */}
              {aiResult && (
                <div className="mt-2 rounded-lg border border-teal-200 bg-teal-50/50 p-3 space-y-2"
                  data-testid="arrival-ai-result">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-bold text-teal-900">
                      AI読み取り結果 — 内容を確認してください
                      <span className="ml-2 font-normal text-teal-700">
                        {aiResult.invoice?.currency}
                        {aiResult.invoice?.invoice_no ? ` · ${aiResult.invoice.invoice_no}` : ''}
                        {aiResult.invoice?.invoice_date ? ` · ${aiResult.invoice.invoice_date}` : ''}
                        {aiResult.pages_read ? ` · ${aiResult.pages_read}/${aiResult.pages}ページ読み取り` : ''}
                      </span>
                    </p>
                    <div className="flex items-center gap-2">
                      <Button type="button" size="sm" onClick={applyAi}
                        className="h-7 text-xs bg-teal-700 hover:bg-teal-800"
                        data-testid="arrival-ai-apply">反映する</Button>
                      <button type="button" onClick={() => setAiResult(null)}
                        className="text-[11px] text-zinc-500 hover:underline"
                        data-testid="arrival-ai-dismiss">閉じる</button>
                    </div>
                  </div>
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="text-left text-zinc-500">
                        <th className="pr-2 font-semibold">依頼明細</th>
                        <th className="pr-2 font-semibold">請求書の該当行</th>
                        <th className="pr-2 font-semibold text-right">金額</th>
                        <th className="font-semibold">確度</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(aiResult.matches || []).map((m) => (
                        <tr key={m.line_index} className="border-t border-teal-100"
                          data-testid={`arrival-ai-match-${m.line_index}`}>
                          <td className="py-0.5 pr-2">{m.name}{m.drawing_no ? ` / ${m.drawing_no}` : ''}</td>
                          <td className="py-0.5 pr-2 text-zinc-600">{m.invoice_text || '—'}</td>
                          <td className="py-0.5 pr-2 text-right tabular-nums">
                            {m.amount == null ? '—' : m.amount.toLocaleString('ja-JP')}
                          </td>
                          <td className={`py-0.5 ${m.confidence >= 80 ? 'text-emerald-700' : 'text-amber-700'}`}>
                            {m.matched ? `${m.confidence}％ ${m.reason}` : '未一致'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {(aiResult.invoice?.extra_costs || []).length > 0 && (
                    <p className="text-[11px] text-teal-900" data-testid="arrival-ai-extras">
                      諸費用: {aiResult.invoice.extra_costs.map((x) => `${x.label} ${x.amount.toLocaleString('ja-JP')}`).join(' / ')}
                    </p>
                  )}
                  {(aiResult.warnings || []).map((w, i) => (
                    <p key={i} className="text-[11px] text-amber-800 flex items-start gap-1"
                      data-testid={`arrival-ai-warning-${i}`}>
                      <Warning size={11} weight="fill" className="mt-[2px] shrink-0" /> {w}
                    </p>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="arrival-cancel">
            キャンセル
          </Button>
          <Button onClick={submit} disabled={saving || loading || uploading}
            className="bg-teal-700 hover:bg-teal-800"
            data-testid="arrival-submit">
            {saving ? '登録中...' : '入荷完了'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    {bridge && (
      <RegisterAsAssetDialog
        open
        order={bridge.order}
        ledger={bridge.ledger}
        onClose={() => { setBridge(null); onSaved?.(); }}
        onRegistered={() => { setBridge(null); onSaved?.(); }}
      />
    )}
    </>
  );
}
